import { useState, useEffect } from 'react';
import Button from "@mui/material/Button";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { scannersList, getScannerLabel } from '../../contexts/ScannersContext';
import { useNavigate } from "react-router";
import { useAuth } from '../../contexts/AuthContext';
import TruCard from '../TruCard';
import ListItem from '../dashboards/items/ListItem';
import * as theme from '../../theme';
import GeneralSelect from '../GeneralSelect';

const DayView = ({ selectedDay, scheduleData, onCancel, onSave }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // State for edited day data
  const [dayData, setDayData] = useState(() => {
    const monthKey = selectedDay.date.format('YYYYMM');
    const dayKey = selectedDay.date.format('DD');
    const existingData = scheduleData[monthKey]?.days?.[dayKey] || {
      date: selectedDay.date.format('YYYY-MM-DD'),
      practitioners: [],
      scheduled: [],
      scansBooked: 0,
      totalScans: 0
    };
    // Initialize practitioners with all scanners, default scans to 0 if not set
    const practitioners = scannersList.map((scanner) => {
      const existing = existingData.practitioners.find((p) => p.id === scanner.value);
      return {
        id: scanner.value,
        scans: existing ? existing.scans : 0,
      };
    });
    return {
      ...existingData,
      practitioners,
      totalScans: practitioners.reduce((sum, p) => sum + p.scans, 0),
    };
  });

  // State for client names
  const [clientNames, setClientNames] = useState({});

  // Fetch client names for scheduled scans
  useEffect(() => {
    const fetchClientNames = async () => {
      const names = {};
      for (const scan of dayData.scheduled) {
        try {
          const practitionerDocRef = doc(db, 'practitioners', scan.practitionerId, 'clients', 'list');
          const practitionerDoc = await getDoc(practitionerDocRef);
          if (practitionerDoc.exists()) {
            const clients = practitionerDoc.data().clients || [];
            const client = clients.find((c) => c.id === scan.clientId);
            if (client) {
              names[scan.scanId] = `${client.firstName} ${client.lastName}`;
            } else {
              names[scan.scanId] = 'Unknown Client';
            }
          } else {
            names[scan.scanId] = 'Unknown Client';
          }
        } catch (error) {
          console.error('Error fetching client name:', error);
          names[scan.scanId] = 'Unknown Client';
        }
      }
      setClientNames(names);
    };

    if (dayData.scheduled.length > 0) {
      fetchClientNames();
    }
  }, [dayData.scheduled]);

  // Update totalScans when practitioners change
  useEffect(() => {
    setDayData((prev) => ({
      ...prev,
      totalScans: prev.practitioners.reduce((sum, p) => sum + p.scans, 0),
    }));
  }, [dayData.practitioners]);

  // Handle increment/decrement scans
  const handleScanChange = (id, increment) => {
    setDayData((prev) => {
      const newPractitioners = prev.practitioners.map((p) => {
        if (p.id === id) {
          if (increment) {
            return { ...p, scans: p.scans + 1 };
          } else {
            const newScans = Math.max(0, p.scans - 1);
            const newTotalScans = prev.practitioners.reduce(
              (sum, prac) => sum + (prac.id === id ? newScans : prac.scans),
              0
            );
            if (newTotalScans >= prev.scansBooked) {
              return { ...p, scans: newScans };
            }
            return p;
          }
        }
        return p;
      });
      return {
        ...prev,
        practitioners: newPractitioners,
        totalScans: newPractitioners.reduce((sum, p) => sum + p.scans, 0),
      };
    });
  };

  // Save handler
  const handleSave = () => {
    const updatedData = { ...dayData };
    onSave(updatedData);
  };

  // Inline CSS styles
  const styles = {
    container: {
      padding: '16px',
      maxWidth: '1600px',
      margin: '0 auto',
    },
    navContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
    },
    title: {
      fontSize: '20px',
      fontWeight: 500,
    },
    grid: {
      display: 'flex',
      justifyContent: 'center',
      gap: '16px',
      width: '100%',
    },
    card: {
      width: '380px',
      boxSizing: 'border-box',
    },
    cardContent: {
      minHeight: '280px',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      justifyContent: 'space-between',
    },
    practitionerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '6px 10px',
      marginBottom: 8,
      backgroundColor: theme.Colors.GrayLight,
      borderRadius: 2,
      fontSize: 14,
    },
    scanRow: {
      display: 'flex',
      justifyContent: 'flex-start',
      padding: '10px 10px',
      marginBottom: 8,
      backgroundColor: theme.Colors.GrayLight,
      borderRadius: 2,
      fontSize: 14,
      cursor: 'pointer',
    },
    scanControls: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    iconButton: {
      cursor: 'pointer',
      color: theme.Colors.GoldDark,
    },
    available: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: '20px',
    },
    formControl: {
      margin: '10px 0',
      minWidth: 120,
    },
  };

  // Determine if user is manager (view-only)
  const isManager = currentUser.role === 'manager';
  // Filter practitioners for manager (only show those with scans > 0)
  const displayedPractitioners = isManager
    ? dayData.practitioners.filter((p) => p.scans > 0)
    : dayData.practitioners;

  return (
    <div style={styles.container}>
      {/* Navigation Buttons */}
      <div style={styles.navContainer}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={onCancel}
        >
          Back
        </Button>
        <h2 style={styles.title}>{selectedDay.date.format('ddd, MMMM D, YYYY').toUpperCase()}</h2>
        {!isManager ? (
          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={handleSave}
          >
            Save
          </Button>
        ) : (
          <div style={{ width: 80 }}></div>
        )}
      </div>

      {/* Day Edit Grid */}
      <div style={styles.grid}>
          <>
            {/* Practitioners Card */}
            <TruCard
              title="SCAN AVAILABILITY"
              styles={styles.card}
              childStyles={styles.cardContent}
            >
              <div>
                {displayedPractitioners.length > 0 ? (
                  displayedPractitioners.map((practitioner) => (
                    <div key={practitioner.id} style={styles.practitionerRow}>
                      <div>{getScannerLabel(practitioner.id)}</div>
                      <div style={styles.scanControls}>
                        {isManager ? (
                          <span>{practitioner.scans}</span>
                        ) : (
                          <>
                            {practitioner.scans > 0 && (
                              <RemoveIcon
                                style={styles.iconButton}
                                onClick={() => handleScanChange(practitioner.id, false)}
                              />
                            )}
                            <span>{practitioner.scans}</span>
                            <AddIcon
                              style={styles.iconButton}
                              onClick={() => handleScanChange(practitioner.id, true)}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center' }}>
                    No scan availability
                  </div>
                )}
              </div>
              <div style={styles.available}>
                <div style={{ fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Available</div>
                <div style={{ fontSize: 20 }}>{dayData.totalScans - dayData.scansBooked} of {dayData.totalScans}</div>
              </div>
            </TruCard>

            {/* Booked Scans Card */}
            <TruCard
              title="BOOKED SCANS"
              styles={styles.card}
              childStyles={styles.cardContent}
            >
              {dayData.scheduled.length > 0 ? (
                <div>
                  {dayData.scheduled.map((scan) => (
                    <ListItem
                      key={scan.scanId}
                      label={clientNames[scan.scanId] || 'Loading...'}
                      onClick={() => navigate(`/clients/${scan.clientId}/scans/${scan.scanId}`)}
                    />
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center' }}>
                  No booked scans yet
                </div>
              )}
            </TruCard>
          </>
      </div>
    </div>
  );
};

export default DayView;